import { useCallback, useEffect, useRef, useState } from 'react';
import { ImportExportAction, ImportExportAdapter } from '../types';
import { DateTime } from 'luxon';
import FileSaver from 'file-saver';
import { toast } from '../../../util/toasts';

export type ImportExportCallbacks = {
  onImportSuccess?: (result: unknown) => void;
  onExportSuccess?: (result: BlobPart) => void;
  onExportError?: (error: unknown) => void;
  onImportError?: (error: unknown) => void;
  onExportSampleSuccess?: () => void;
  onExportSampleError?: (error: unknown) => void;
};

export const DEFAULT_FILE_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const FILE_EXTENSION = '.xlsx';

type ImportExportModalStateParams<TI, TE, TAdapter extends ImportExportAdapter<TI, TE>> = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initialImportParams: TI;
  initialExportParams: TE;
  adapter: TAdapter;
  action: ImportExportAction;
  callbacks?: ImportExportCallbacks;
};

// Utility function to format date for file names
export const formatDateForFileName = (date: DateTime = DateTime.local()): string => {
  return date.toFormat('D').replaceAll('/', '-');
};

export const saveFile = (blob: Blob, fileName: string): void => {
  FileSaver.saveAs(blob, fileName);
};

export const useImportExportModalState = <TI, TE, TAdapter extends ImportExportAdapter<TI, TE>>({
  isOpen,
  setIsOpen,
  initialImportParams,
  initialExportParams,
  adapter,
  action,
  callbacks,
}: ImportExportModalStateParams<TI, TE, TAdapter>) => {
  const [isSaving, setIsSaving] = useState(false);
  const [file, setFile] = useState<File>();
  const [importParams, setImportParams] = useState<TI>(initialImportParams);
  const [exportParams, setExportParams] = useState<TE>(initialExportParams);
  const hasTriggeredExport = useRef(false);

  const close = useCallback(() => {
    if (isSaving) return;
    setFile(undefined);
    setIsOpen(false);
    hasTriggeredExport.current = false;
  }, [isSaving, setIsOpen, hasTriggeredExport, setFile]);

  // Reset the export trigger flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasTriggeredExport.current = false;
    }
  }, [isOpen]);

  const onExport = useCallback(async () => {
    try {
      setIsSaving(true);
      const file = await adapter.export(exportParams);

      const exportFileName = adapter.defaultExportFileName?.(exportParams) ?? `export_${formatDateForFileName()}`;
      const fileType = adapter.fileAccept ?? DEFAULT_FILE_TYPE;

      const data = new Blob([file], { type: fileType });
      saveFile(data, `${exportFileName}${FILE_EXTENSION}`);

      callbacks?.onExportSuccess?.(file) ?? toast('Export successful', 'success');
      close();
    } catch (error) {
      callbacks?.onExportError?.(error) ?? toast('Error exporting', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [adapter, exportParams, callbacks, close]);

  // Per the design, export should happen automatically on export modal open, on first open only
  useEffect(() => {
    if (action === 'export' && isOpen && !hasTriggeredExport.current) {
      hasTriggeredExport.current = true;
      onExport();
    }
  }, [action, isOpen, onExport]);

  const onImport = useCallback(async () => {
    try {
      if (!file) return;

      setIsSaving(true);
      const result = await adapter.import(file, importParams);
      callbacks?.onImportSuccess?.(result) ?? toast('Import successful', 'success');
      close();
    } catch (error) {
      callbacks?.onImportError?.(error) ?? toast('Error importing', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [file, adapter, importParams, callbacks, close]);

  const exportSampleFile = useCallback(async () => {
    try {
      if (!adapter.exportSample) return;

      setIsSaving(true);

      const file = await adapter.exportSample();

      const exportFileName = `sample_${adapter.typeLabel}_${formatDateForFileName()}`;
      const fileType = adapter.fileAccept ?? DEFAULT_FILE_TYPE;

      const data = new Blob([file], { type: fileType });
      saveFile(data, `${exportFileName}${FILE_EXTENSION}`);
      callbacks?.onExportSampleSuccess?.() ?? toast('Sample file exported successfully', 'success');
    } catch (error) {
      callbacks?.onExportSampleError?.(error) ?? toast('Error exporting sample file', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [adapter, callbacks]);

  return {
    isSaving,
    setIsSaving,
    file,
    setFile,
    importParams,
    setImportParams,
    exportParams,
    setExportParams,
    hasTriggeredExport,
    close,
    onExport,
    onImport,
    exportSampleFile,
  };
};
